import { useState, useMemo } from "react";
import { MapPin, ChevronDown } from "lucide-react";
import { BIRTH_COUNTRIES } from "@/data/birthPlaceCountries";

interface BirthPlaceSelectProps {
  value: string;
  onChange: (value: string) => void;
}

const BirthPlaceSelect = ({ value, onChange }: BirthPlaceSelectProps) => {
  // Parse current value "City, Country"
  const parts = value.split(",").map((s) => s.trim());
  const currentCity = parts[0] || "";
  const currentCountry = parts[1] || "";

  const selectedCountryData = useMemo(
    () => BIRTH_COUNTRIES.find((c) => c.name === currentCountry),
    [currentCountry]
  );

  const handleCountryChange = (countryName: string) => {
    if (!countryName) {
      onChange("");
      return;
    }
    onChange(`, ${countryName}`);
  };

  const handleCityChange = (city: string) => {
    if (!city) {
      onChange(`, ${currentCountry}`);
      return;
    }
    onChange(`${city}, ${currentCountry}`);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Country select */}
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <select
          value={currentCountry}
          onChange={(e) => handleCountryChange(e.target.value)}
          className="w-full bg-muted rounded-2xl pl-10 pr-10 py-3 text-sm font-medium text-foreground outline-none appearance-none cursor-pointer min-h-[48px]"
          aria-label="Pays de naissance"
        >
          <option value="">Sélectionner un pays...</option>
          {BIRTH_COUNTRIES.map((c) => (
            <option key={c.name} value={c.name}>
              {c.flag} {c.name}
            </option>
          ))}
        </select>
      </div>

      {/* City select — only shown after country is selected */}
      {currentCountry && selectedCountryData && (
        <div className="relative">
          <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <select
            value={currentCity}
            onChange={(e) => handleCityChange(e.target.value)}
            className="w-full bg-muted rounded-2xl pl-10 pr-10 py-3 text-sm font-medium text-foreground outline-none appearance-none cursor-pointer min-h-[48px]"
            aria-label="Ville de naissance"
          >
            <option value="">Sélectionner une ville...</option>
            {selectedCountryData.cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default BirthPlaceSelect;
